import { useEffect, useState } from "react";
import { tr } from "date-fns/locale";
import { Plane, Upload, Eye, CalendarDays } from "lucide-react";
import { api, formatApiError, formatTL, formatDate } from "@/lib/api";
import { DRONE_STATUS, SLOT_LABEL, ymd, openReceipt } from "@/lib/shootOptions";
import { HourPicker, DroneCreditsPanel } from "@/components/drone/DronePlus";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const today = new Date(new Date().setHours(0, 0, 0, 0));

const RequestCard = ({ d, onReschedule, onUploaded }) => {
  const st = DRONE_STATUS[d.status];
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/drone/requests/${d.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dekont yüklendi, onay bekleniyor");
      onUploaded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    e.target.value = "";
  };
  return (
    <Card data-testid={`drone-request-${d.id}`} className="p-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-slate-900">{formatDate(d.date)} · {d.hour || SLOT_LABEL[d.slot]}{d.city ? ` · ${d.city}` : ""}</span>
        <Badge variant="outline" data-testid={`drone-status-${d.id}`} className={st.cls}>{st.label}</Badge>
        <Badge variant="outline">{d.is_free ? "Paket hakkı" : `Ücretli · ${formatTL(d.total)}`}</Badge>
      </div>
      <div className="text-xs text-slate-500">{d.address} · Talep eden: {d.requested_by_name}</div>
      {d.reject_reason && <div className="text-xs text-red-700">Not: {d.reject_reason}</div>}
      {d.status === "AWAITING_PAYMENT" && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm space-y-2">
          <div>Tutar: <b>{formatTL(d.price)}</b> + KDV {formatTL(d.vat)} = <b data-testid={`drone-total-${d.id}`}>{formatTL(d.total)}</b> · Referans: <b className="font-mono" data-testid={`drone-ref-${d.id}`}>{d.reference_code}</b></div>
          <label className="inline-flex items-center gap-2 cursor-pointer text-emerald-700 font-medium">
            <Upload className="w-4 h-4" /> Dekont yükle (PDF/görsel)
            <input data-testid={`drone-receipt-input-${d.id}`} type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} />
          </label>
        </div>
      )}
      <div className="flex gap-2">
        {d.status === "DATE_REJECTED" && <Button size="sm" data-testid={`drone-reschedule-btn-${d.id}`} onClick={() => onReschedule(d)}><CalendarDays className="w-4 h-4 mr-1" />Yeni tarih seç</Button>}
        {d.receipt && <Button size="sm" variant="outline" data-testid={`drone-receipt-view-${d.id}`} onClick={() => openReceipt(`/drone/requests/${d.id}/receipt`)}><Eye className="w-4 h-4 mr-1" />Dekont</Button>}
      </div>
    </Card>
  );
};

const QuotaCard = ({ ov }) => (
  <Card className="p-4 space-y-1" data-testid="drone-quota-card">
    <div className="text-xs uppercase tracking-wider text-slate-400">Bu ay ücretsiz hak</div>
    <div className="text-2xl font-bold text-slate-900" data-testid="drone-quota-remaining">{ov.quota?.remaining ?? 0} / {ov.quota?.free_per_month ?? 0}</div>
    <div className="text-xs text-slate-500">Hak bittiğinde ek çekim: {formatTL(ov.price)} + %{ov.vat_rate} KDV</div>
    <div className="text-[11px] text-slate-500 pt-1 border-t mt-2">{ov.payment_info}</div>
  </Card>
);

export default function DronePage() {
  const [month, setMonth] = useState(new Date());
  const [ov, setOv] = useState(null);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [requests, setRequests] = useState([]);
  const [resched, setResched] = useState(null);
  const [config, setConfig] = useState(null);
  const [cityId, setCityId] = useState("");
  const [district, setDistrict] = useState("");
  const loadCfg = () => api.get("/drone/v2/config").then((r) => {
    setConfig(r.data);
    setCityId((c) => c || (r.data.cities.find((x) => x.name.toLocaleLowerCase("tr") === (r.data.office_city || "").toLocaleLowerCase("tr")) || r.data.cities[0] || {}).id || "");
  });
  useEffect(() => { loadCfg(); }, []);

  const mk = ymd(month).slice(0, 7);
  const loadOv = () => api.get("/drone/overview", { params: { month: mk } }).then((r) => setOv(r.data));
  const loadReq = () => api.get("/drone/requests").then((r) => setRequests(r.data));
  useEffect(() => { loadOv(); }, [mk]);
  useEffect(() => { loadReq(); }, []);

  const taken = (date, s) => ov?.booked.some((b) => b.date === date && b.slot === s);
  const full = (d) => taken(ymd(d), "MORNING") && taken(ymd(d), "AFTERNOON");
  const willPay = !resched && ov?.quota && ov.quota.remaining === 0 && !(config?.balance?.MANUAL > 0 || config?.balance?.PURCHASED > 0);

  const submit = async (e) => {
    e.preventDefault();
    const body = { date: ymd(day), hour: slot, city_id: cityId, district: district || null, address, notes };
    try {
      if (resched) await api.put(`/drone/v2/requests/${resched.id}/reschedule`, body);
      else await api.post("/drone/v2/requests", body);
      toast.success("Talebiniz Süper Admin onayına gönderildi");
      setDay(null); setSlot(null); setResched(null); setAddress(""); setNotes("");
      loadOv(); loadReq(); loadCfg();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const startResched = (d) => { setResched(d); setAddress(d.address); setNotes(d.notes || ""); setSlot(d.proposal?.hour || null); setDay(d.proposal ? new Date(`${d.proposal.date}T12:00:00`) : null); if (d.proposal?.city_id || d.city_id) setCityId(d.proposal?.city_id || d.city_id); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="space-y-6" data-testid="drone-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Drone Çekim Talebi</h1>
        <p className="text-slate-500 text-sm mt-1">Takvimden boş bir gün ve saat seçin. Talepler Süper Admin onayından sonra kesinleşir.</p>
      </div>
      {resched && <div data-testid="drone-reschedule-banner" className="rounded-lg bg-sky-50 border border-sky-200 p-3 text-sm flex justify-between">Reddedilen talep ({formatDate(resched.date)}) için yeni tarih seçiyorsunuz.<button className="text-sky-700 underline" onClick={() => setResched(null)}>Vazgeç</button></div>}

      <div className="grid lg:grid-cols-[auto_1fr_280px] gap-6 items-start">
        <Card className="p-3">
          <Calendar data-testid="drone-calendar" mode="single" locale={tr} month={month} onMonthChange={setMonth} selected={day}
            onSelect={(d) => { setDay(d); setSlot(null); }} disabled={[{ before: today }, full]}
            modifiers={{ full }} modifiersClassNames={{ full: "line-through text-red-400" }} />
        </Card>
        <Card className="p-5">
          {!day ? (
            <div className="text-center text-slate-500 text-sm py-10"><Plane className="w-8 h-8 mx-auto mb-2 text-slate-300" />Takvimden bir gün seçin</div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="font-semibold">{day.toLocaleDateString("tr-TR", { dateStyle: "full" })}</div>
              <HourPicker config={config} cityId={cityId} setCityId={setCityId} district={district} setDistrict={setDistrict} day={day} hour={slot} setHour={setSlot} exclude={resched?.id} />
              {resched?.proposal && <div className="text-xs text-sky-700" data-testid="drone-proposal-note">Temsilci önerisi: {resched.proposal.date} {resched.proposal.hour}</div>}
              <div><Label>Çekim adresi *</Label><Input data-testid="drone-address-input" required minLength={3} value={address} onChange={(e) => setAddress(e.target.value)} /></div>
              <div><Label>Not</Label><Input data-testid="drone-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              {willPay && <div data-testid="drone-paid-warning" className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">Bu ayki ücretsiz hakkınız doldu. Bu talep ücretlidir: {formatTL(config?.cities.find((c) => c.id === cityId)?.price ?? ov.price)} + KDV. Tarih onaylandıktan sonra referans kodu ile ödeme yapıp dekont yüklemeniz istenecek.</div>}
              <Button data-testid="drone-submit-btn" type="submit" disabled={!slot} className="w-full bg-emerald-600 hover:bg-emerald-700">{resched ? "Yeni tarihi gönder" : "Talep oluştur"}</Button>
            </form>
          )}
        </Card>
        {ov && <QuotaCard ov={ov} />}
      </div>

      <DroneCreditsPanel config={config} onChanged={loadCfg} />

      <section className="space-y-2">
        <h2 className="text-base md:text-lg font-semibold text-slate-900">Taleplerim</h2>
        {requests.length === 0 ? <Card className="p-8 text-center text-sm text-slate-500">Henüz drone talebi yok</Card>
          : requests.map((d) => <RequestCard key={d.id} d={d} onReschedule={startResched} onUploaded={loadReq} />)}
      </section>
    </div>
  );
}
